import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import moment from 'moment';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

/**
 * @typedef {object} Cis2SessionTimeLeftProps
 * @property {string} [displayFormat]
 * @property {import('@mui/system').SystemProps<import('@mui/material/styles').Theme>} [sx]
 */

const formatTimeLeft = (secondsLeft) => {
    if (!Number.isFinite(secondsLeft)) {
        return '--:--';
    }

    const safeSecondsLeft = Math.max(0, secondsLeft);

    if (safeSecondsLeft >= 3600) {
        return `${String(Math.floor(safeSecondsLeft / 3600)).padStart(2, '0')}:${String(
            Math.floor((safeSecondsLeft % 3600) / 60),
        ).padStart(2, '0')}:${String(safeSecondsLeft % 60).padStart(2, '0')}`;
    }

    return `${String(Math.floor(safeSecondsLeft / 60)).padStart(2, '0')}:${String(safeSecondsLeft % 60).padStart(2, '0')}`;
};

/**
 * @param {Cis2SessionTimeLeftProps} props
 */
const Cis2SessionTimeLeft = ({ displayFormat, sx }) => {
    const cis2data = useSelector((state) => state.cis2);
    const [currentTime, setCurrentTime] = useState(moment());

    useEffect(() => {
        let interval = -1;

        if (cis2data?.expiresDate) {
            interval = setInterval(() => {
                setCurrentTime(moment());
            }, 1000);
        }

        return () => {
            if (interval > -1) {
                clearInterval(interval);
            }
        };
    }, [cis2data?.expiresDate]);

    const secondsLeft = moment(cis2data?.expiresDate).diff(currentTime, 'seconds');
    const timeLeft = formatTimeLeft(secondsLeft);

    const secondsLeft2 = moment(cis2data?.refreshExpiresDate).diff(currentTime, 'seconds');
    const timeLeft2 = formatTimeLeft(secondsLeft2);

    const resolvedDisplayFormat = String(displayFormat ?? '')
        .replaceAll('{timeLeft}', timeLeft)
        .replaceAll('{timeLeft2}', timeLeft2);

    return (
        <Typography variant="body2" color="text.secondary" gutterBottom sx={sx}>
            {resolvedDisplayFormat}
        </Typography>
    );
};

Cis2SessionTimeLeft.defaultProps = {
    displayFormat: 'Logged into CIS2 ({timeLeft}) ({timeLeft2})',
};
Cis2SessionTimeLeft.propTypes = {
    displayFormat: PropTypes.string,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

export default Cis2SessionTimeLeft;
